import styles from "./PaymentDrawer.module.scss";

import * as React from "react";
import Box from "@mui/material/Box";

import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import CheckIcon from "@mui/icons-material/Check";

import {
    greyState,
    ortexColorState,
    textColorState,
    paymentModalOpenState,
    appUpgradeLoadingState,
    subscriptionOfferingsState,
    backgroundColorState,
    darkerBackgroundColorState,
    lighterBackgroundColorState,
    themeState,
    paymentDrawerOpenState,
    planPickerSelectedPackageState,
} from "@/lib/store";

import { Browser } from "@capacitor/browser";

import Image from "next/image";
import { Chip, CircularProgress, Drawer, IconButton } from "@mui/material";

import { useRecoilState, useRecoilValue } from "recoil";
import { isOrtexApp } from "@/lib/utils";
import useOpenSubscriptionForm from "src/hooks/useOpenSubscriptionForm";
import { UPGRADE_LOCATIONS, trackUpgradeClick } from "@/lib/tracking";

const PaymentDrawer: React.FC = () => {
    const { openSubscriptionForm } = useOpenSubscriptionForm();

    const [open, setOpen] = useRecoilState(paymentDrawerOpenState);
    const [preSelectedPackage, setPreSelectedPackage] = useRecoilState(planPickerSelectedPackageState);

    const grey = useRecoilValue(greyState);
    const textColor = useRecoilValue(textColorState);
    const ortexColor = useRecoilValue(ortexColorState);
    const theme = useRecoilValue(themeState);
    const backgroundColor = useRecoilValue(backgroundColorState);
    const darkerBackgroundColor = useRecoilValue(darkerBackgroundColorState);
    const lighterBackgroundColor = useRecoilValue(lighterBackgroundColorState);
    const appUpgradeLoading = useRecoilValue(appUpgradeLoadingState);
    const offerings = useRecoilValue(subscriptionOfferingsState) as any[] | null;

    const packageTypeOrder: Record<string, number> = { WEEKLY: 0, MONTHLY: 1, ANNUAL: 2 };
    const validPackages = React.useMemo(
        () =>
            (offerings ?? [])
                .filter((pkg: any) => !pkg.product?.title?.toLowerCase().includes("basic") && !pkg.identifier?.toLowerCase().includes("basic"))
                .sort((a: any, b: any) => (packageTypeOrder[a.packageType] ?? 99) - (packageTypeOrder[b.packageType] ?? 99)),
        [offerings]
    );

    const [selectedPackage, setSelectedPackage] = React.useState<any>(() => preSelectedPackage ?? validPackages[0] ?? null);

    React.useEffect(() => {
        if (open) {
            setSelectedPackage(preSelectedPackage ?? validPackages[0] ?? null);
            if (preSelectedPackage) setPreSelectedPackage(null);
        }
    }, [open]);

    const openAppBrowserTerms = async () => {
        await Browser.open({ url: "https://public.ortex.com/terms-and-conditions" });
    };

    const openAppBrowserPrivacy = async () => {
        await Browser.open({ url: "https://public.ortex.com/privacypolicy" });
    };
    const listItems = ["Live short interest", "ORTEX trading signals", "Options data and analysis", "Stock stamps", "Insider data and analysis", "ORTEX stock scores"];

    const [paymentModalOpen, setPaymentModalOpen] = useRecoilState(paymentModalOpenState);

    const handleUpgrade = () => {
        trackUpgradeClick(UPGRADE_LOCATIONS.PAYMENT_DRAWER, { package_type: selectedPackage?.packageType });
        if (!isOrtexApp()) {
            setPaymentModalOpen(true);
            setTimeout(function () {
                setPaymentModalOpen(false);
            }, 10000);
        }
        openSubscriptionForm(true, selectedPackage);
    };

    const getPlanLabel = (pkg: any) => {
        const type = pkg.packageType;
        if (type === "ANNUAL") return "Annual";
        if (type === "MONTHLY") return "Monthly";
        if (type === "WEEKLY") return "Weekly";
        return pkg.product?.title ?? pkg.identifier;
    };

    const isBasic = (pkg: any) => {
        const title: string = pkg.product?.title ?? "";
        const id: string = pkg.identifier ?? "";
        return title.toLowerCase().includes("basic") || id.toLowerCase().includes("basic");
    };

    return (
        <Drawer
            variant="temporary"
            ModalProps={{
                keepMounted: false,
            }}
            anchor="bottom"
            sx={{ height: "100vh" }}
            PaperProps={{ sx: { height: "100%", display: "flex", alignItems: "space-around", justifyContent: "space-between", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" } }}
            open={open}
        >
            <Box sx={{ height: "22%", textAlign: "center", width: "100%" }}>
                <Image unoptimized src="/assets/ortex-logo-v.png" alt="ortex-logo" width="180" height="100" />
                <Typography sx={{ fontSize: "24px", marginTop: 2, fontWeight: 800 }}>
                    Try <Chip size="small" sx={{ color: "#fff", fontWeight: 500, paddingX: 1, paddingY: 1.5 }} color="primary" label={"ADVANCED"} /> for free
                </Typography>
                <IconButton sx={{ color: textColor, position: "absolute", top: 0, right: 10, paddingTop: "env(safe-area-inset-top)" }} onClick={() => setOpen(false)}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box className={styles.list} sx={{ height: "38%" }}>
                <IconButton
                    disabled
                    sx={{
                        width: "45px",
                        height: "45px",
                        marginX: "auto",
                        backgroundColor: "black",
                        color: "#fff",
                        boxShadow: "0px 3px 6px #00000029",
                        ":disabled": {
                            backgroundColor: "black",
                            color: "#fff",
                        },
                    }}
                >
                    <LockIcon sx={{ fontSize: "24px" }} />
                </IconButton>
                <Typography sx={{ gridColumn: "2/4", fontSize: "24px", fontWeight: 800 }}>Unlock everything</Typography>
                <Typography sx={{ gridColumn: "2/4", fontSize: "16px", maxWidth: "300px" }}>Elevate your trading with ORTEX full access Advanced subscription.</Typography>
                <Typography sx={{ gridColumn: "2/4", fontSize: "14px", fontWeight: "300", marginTop: 3 }}>Includes</Typography>
                {listItems.map((item) => {
                    return (
                        <Box sx={{ gridColumn: "2/4", display: "flex", alignItems: "center", justifyContent: "flex-start" }} key={item}>
                            <IconButton
                                key={item}
                                disabled
                                size="small"
                                sx={{
                                    width: "25px",
                                    height: "25px",
                                    backgroundColor: "black",
                                    color: "#fff",
                                    boxShadow: "0px 3px 6px #00000029",
                                    ":disabled": {
                                        backgroundColor: "black",
                                        color: "#fff",
                                    },
                                }}
                            >
                                <CheckIcon sx={{ fontSize: "18px" }} />
                            </IconButton>
                            <Typography sx={{ fontWeight: 700, fontSize: "18px", marginLeft: 2 }}>{item}</Typography>
                        </Box>
                    );
                })}
            </Box>
            {validPackages.length > 0 && (
                <Box sx={{ width: "100%", paddingX: 2, display: "flex", gap: 1, justifyContent: "center" }}>
                    {validPackages.map((pkg) => {
                        const isSelected = selectedPackage?.identifier === pkg.identifier;
                        const label = getPlanLabel(pkg);
                        const price = pkg.product?.priceString ?? "";
                        const basic = isBasic(pkg);
                        return (
                            <Box
                                key={pkg.identifier}
                                onClick={() => setSelectedPackage(pkg)}
                                sx={{
                                    flex: 1,
                                    maxWidth: "120px",
                                    border: isSelected ? "2px solid #31aba6" : `2px solid ${theme === "light" ? lighterBackgroundColor : backgroundColor}`,
                                    borderRadius: "12px",
                                    padding: "10px 8px",
                                    textAlign: "center",
                                    cursor: "pointer",
                                    backgroundColor: isSelected ? darkerBackgroundColor : "transparent",
                                    "&:hover": { backgroundColor: darkerBackgroundColor, borderColor: !isSelected && darkerBackgroundColor },
                                    transition: "all 0.15s ease",
                                }}
                            >
                                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: textColor, lineHeight: 1.2 }}>{basic ? "Basic" : label}</Typography>
                                {!basic && <Typography sx={{ fontSize: "11px", fontWeight: 400, color: grey, lineHeight: 1.2 }}>Advanced</Typography>}
                                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: textColor, marginTop: "4px" }}>{price}</Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}
            <Box sx={{ height: "15%", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <Button
                    size="large"
                    variant="contained"
                    color="inherit"
                    sx={{
                        width: "354px",
                        backgroundColor: "#000",
                        color: "#fff",
                        borderRadius: "30px",
                        textTransform: "initial",
                        boxShadow: "0px 3px 3px #00000061",
                        "&:hover": {
                            backgroundColor: "#000",
                        },
                        "&:active": {
                            backgroundColor: "#000",
                        },
                        "&:disabled": {
                            backgroundColor: "#000",
                        },
                    }}
                    disabled={appUpgradeLoading || paymentModalOpen || (isOrtexApp() && !selectedPackage)}
                    onClick={handleUpgrade}
                >
                    {appUpgradeLoading || paymentModalOpen ? <CircularProgress size={24} /> : "Start my 7-day free trial now"}
                </Button>
            </Box>
            <Box sx={{ height: "5%", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Button
                    variant="text"
                    color="inherit"
                    sx={{ color: grey, textTransform: "initial", padding: 0.5, marginRight: 2 }}
                    href={isOrtexApp() ? undefined : "https://public.ortex.com/privacypolicy"}
                    target="_blank"
                    onClick={() => (isOrtexApp() ? openAppBrowserPrivacy() : undefined)}
                >
                    Privacy Policy
                </Button>
                <Button
                    variant="text"
                    color="inherit"
                    sx={{ color: grey, textTransform: "initial", padding: 0.5, marginLeft: 2 }}
                    href={isOrtexApp() ? undefined : "https://public.ortex.com/terms-and-conditions"}
                    target="_blank"
                    onClick={() => (isOrtexApp() ? openAppBrowserTerms() : undefined)}
                >
                    Terms & Conditions
                </Button>
            </Box>
        </Drawer>
    );
};

export default PaymentDrawer;
